import React from 'react';
import {
  Box,
  Chip,
  ChipProps,
  Container,
  Stack,
  Theme,
  Typography,
} from '@mui/material';
import { tabClasses } from '@mui/material/Tab';
// eslint-disable-next-line import/no-extraneous-dependencies
import { SystemStyleObject } from '@mui/system';

import mixinSx from '@watershed/style/mixinSx';
import BetaLabel from '@watershed/ui-core/components/BetaLabel';
import CalloutChip from '@watershed/ui-core/components/CalloutChip';
import useDocumentTitle from '@watershed/ui-core/hooks/useDocumentTitle';

import Breadcrumb, { BreadcrumbProps } from './Breadcrumb';

export const PAGE_HEADER_Z_INDEX = 3;
export const PAGE_HEADER_HEIGHT = 57;

const rootSx: SystemStyleObject<Theme> = {
  color: (theme) => theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: (theme) => theme.spacing(3),
  position: 'relative',
  minHeight: PAGE_HEADER_HEIGHT,
  flexShrink: 0,
};

const containerSx: SystemStyleObject<Theme> = {
  padding: (theme) => theme.spacing(4, 0),
  marginBottom: (theme) => theme.spacing(2),
};

const fullWidthSx: SystemStyleObject<Theme> = {
  zIndex: PAGE_HEADER_Z_INDEX,
  paddingX: (theme) => theme.spacing(2),
  paddingY: (theme) => theme.spacing(1.5),
  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
  backgroundColor: (theme) => theme.palette.background.paper,
};

const containerCompactSx: SystemStyleObject<Theme> = {
  gridTemplateColumns: '1fr auto',
  paddingX: (theme) => theme.spacing(2),
  paddingY: (theme) => theme.spacing(1.5),
  marginBottom: (theme) => theme.spacing(0),
};

const headerWithTabsSx: SystemStyleObject<Theme> = {
  borderBottom: 0,
  minHeight: PAGE_HEADER_HEIGHT - 1, // 57 (see rootSx) - 1px border
};

const titleSx: SystemStyleObject<Theme> = {
  lineHeight: 1.125,
};

const subtitleSx: SystemStyleObject<Theme> = {
  mt: 1,
  textWrap: 'balance',
};

const tabsSx: SystemStyleObject<Theme> = {
  marginTop: (theme) => theme.spacing(-1),
  paddingLeft: (theme) => theme.spacing(2),
  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
  backgroundColor: (theme) => theme.palette.background.paper,
  [`& .${tabClasses.root}`]: {
    paddingBlock: 1.5, // Default padding top is different if there's an icon, this overrides it to be consistent
  },
};

export interface PageTitleProps {
  chip?: ChipProps;
  breadcrumb?: BreadcrumbProps;
  sectionName?: React.ReactNode;
  title: string;
  renderTitle?: ({
    title,
    titleNode,
  }: {
    title: string;
    titleNode: React.ReactNode;
  }) => React.ReactNode;
  subtitle?: React.ReactNode;
  titleRightCol?: React.ReactNode;
  renderTitleRightCol?: ({
    titleRightCol,
  }: {
    titleRightCol: React.ReactNode;
  }) => React.ReactNode;
  fullWidth?: boolean;
}

const PageTitle = ({
  title,
  chip,
  renderTitle,
  sectionName,
  subtitle,
  breadcrumb,
  titleRightCol,
  renderTitleRightCol,
  fullWidth,
}: PageTitleProps) => {
  const titleNode = (
    <Typography variant="h2" id="title" sx={titleSx}>
      {title}
    </Typography>
  );

  return (
    <Box maxWidth="100%" minWidth={0} width={fullWidth ? '100%' : undefined}>
      {breadcrumb && <Breadcrumb breadcrumb={breadcrumb} />}
      {sectionName && !breadcrumb && (
        <Typography variant="body2" gutterBottom>
          {sectionName}
        </Typography>
      )}
      <Stack direction="row" alignItems="center" gap={1}>
        {renderTitle ? renderTitle({ title, titleNode }) : titleNode}
        {chip ? (
          chip.label === 'Beta' ? (
            <BetaLabel />
          ) : chip.label === 'Alpha' ? (
            <CalloutChip label={chip.label} />
          ) : (
            <Chip {...chip} />
          )
        ) : null}
        {titleRightCol
          ? renderTitleRightCol
            ? renderTitleRightCol({ titleRightCol })
            : titleRightCol
          : null}
      </Stack>
      {subtitle && (
        <Typography component="div" variant="body2" sx={subtitleSx}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

export interface PageHeaderProps {
  heading?: PageTitleProps;
  rightCol?: React.ReactNode;
  rightColSx?: SystemStyleObject<Theme>;
  renderRightCol?: (props: {
    rightCol: React.ReactNode;
    rightColSx?: SystemStyleObject<Theme>;
  }) => React.ReactNode;
  maxWidth?: 'lg' | 'md' | 'sm' | 'xl' | 'xs' | false;
  height?: number;
  isCompact?: boolean;
  tabs?: React.ReactNode;
  sx?: SystemStyleObject<Theme>;
  documentTitle?: string;
}

const PageHeader = (props: PageHeaderProps) => {
  const {
    heading,
    maxWidth,
    rightCol,
    isCompact,
    tabs,
    rightColSx,
    renderRightCol,
    documentTitle,
  } = props;
  const renderedTitle = heading ? <PageTitle {...heading} /> : null;
  const renderedRightCol = rightCol ? (
    renderRightCol ? (
      renderRightCol({ rightCol, rightColSx })
    ) : (
      <Box sx={mixinSx({ justifySelf: 'end', flexShrink: 0 }, rightColSx)}>
        {rightCol}
      </Box>
    )
  ) : null;
  useDocumentTitle(documentTitle ?? heading?.title);
  if (maxWidth) {
    return (
      <Container
        disableGutters
        maxWidth={maxWidth}
        component="header"
        sx={mixinSx(
          rootSx,
          containerSx,
          isCompact ? containerCompactSx : {},
          { height: props.height },
          props.sx
        )}
      >
        {renderedTitle}
        {renderedRightCol}
      </Container>
    );
  } else {
    return (
      <>
        <Box
          component="header"
          sx={mixinSx(
            rootSx,
            fullWidthSx,
            isCompact ? containerCompactSx : {},
            tabs ? headerWithTabsSx : {},
            { height: props.height },
            props.sx
          )}
        >
          {renderedTitle}
          {renderedRightCol}
        </Box>
        {tabs && <Box sx={tabsSx}>{tabs}</Box>}
      </>
    );
  }
};

export default PageHeader;
